import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { GOOGLE_MAPS_API_KEY } from "@/lib/googleMaps";
import type { ListingsProperty } from "@/components/ListingsPropertyCard";

interface Props {
  properties: ListingsProperty[];
  onMarkerClick?: (property: ListingsProperty) => void;
  highlightedId?: string | null;
}

let optionsSet = false;
if (!optionsSet) {
  setOptions({ key: GOOGLE_MAPS_API_KEY, v: "weekly" });
  optionsSet = true;
}

const ListingsMap = ({ properties, onMarkerClick, highlightedId }: Props) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    importLibrary("maps").then(() => {
      if (!mapRef.current) return;
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 20, lng: 0 },
        zoom: 2,
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!loaded || !mapInstance.current) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidCoords = false;

    const validProperties = properties.filter(
      (p) => p.latitude != null && p.longitude != null
    );

    validProperties.forEach((prop) => {
      const position = { lat: prop.latitude!, lng: prop.longitude! };
      bounds.extend(position);
      hasValidCoords = true;

      const marker = new window.google.maps.Marker({
        map: mapInstance.current,
        position,
        title: prop.masked_title || prop.original_name || "Property",
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "hsl(160,84%,39%)",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
      });

      marker.addListener("click", () => {
        if (!infoWindowRef.current || !mapInstance.current) return;
        onMarkerClick?.(prop);

        const imgHtml = prop.image_url
          ? `<img src="${prop.image_url}" alt="${prop.masked_title || "Property"}" style="width:180px;height:120px;object-fit:cover;border-radius:8px;margin-bottom:8px;" />`
          : `<div style="width:180px;height:120px;background:#f0f0f0;border-radius:8px;margin-bottom:8px;display:flex;align-items:center;justify-content:center;color:#999;">No image</div>`;

        infoWindowRef.current.setContent(`
          <div style="max-width:200px;font-family:system-ui,sans-serif;">
            ${imgHtml}
            <p style="font-weight:600;font-size:13px;margin:0 0 4px;color:#1a1a1a;">
              ${prop.masked_title || prop.original_name || "Untitled Property"}
            </p>
            <p style="font-size:12px;color:#666;margin:0 0 8px;">
              ${prop.city || ""}${prop.city && prop.country ? ", " : ""}${prop.country || ""}
            </p>
            <a href="/listings/${prop.id}" style="font-size:12px;color:hsl(160,84%,39%);text-decoration:none;font-weight:500;">
              View Details →
            </a>
          </div>
        `);
        infoWindowRef.current.open(mapInstance.current, marker);
      });

      markersRef.current.push(marker);
    });

    if (hasValidCoords && validProperties.length > 1) {
      mapInstance.current.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    } else if (hasValidCoords && validProperties.length === 1) {
      mapInstance.current.setCenter({
        lat: validProperties[0].latitude!,
        lng: validProperties[0].longitude!,
      });
      mapInstance.current.setZoom(12);
    }
  }, [properties, loaded, onMarkerClick]);

  useEffect(() => {
    if (!loaded) return;
    const validProperties = properties.filter(
      (p) => p.latitude != null && p.longitude != null
    );
    markersRef.current.forEach((marker, i) => {
      const isHighlighted = validProperties[i]?.id === highlightedId;
      marker.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: isHighlighted ? 14 : 10,
        fillColor: isHighlighted ? "hsl(160,84%,30%)" : "hsl(160,84%,39%)",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: isHighlighted ? 3 : 2,
      });
      marker.setZIndex(isHighlighted ? 10 : 1);
    });
  }, [highlightedId, loaded, properties]);

  return (
    <div ref={mapRef} className="w-full h-full rounded-2xl min-h-[400px]" />
  );
};

export default ListingsMap;
